// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import { useSwiper } from "swiper/react";
/* eslint-enable spire/fenced-imports */

import { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import ArrowDown from "@insite/mobius/Icons/ArrowDown";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import { getProp } from "@insite/mobius/utilities";
import { ArrowPosition, ArrowType, NavigationProps, useArrowElement } from "@werner/App/Components/XngCarousel";
import CaretLeft from "@werner/XDL/Icons/CaretLeft";
import CaretRight from "@werner/XDL/Icons/CaretRight";
import ChevronLeft from "@werner/XDL/Icons/ChevronLeft";
import ChevronRight from "@werner/XDL/Icons/ChevronRight";
import { color, xClassNames } from "@werner/XDL/Utilities";
import React, { FC, useCallback } from "react";
import { css } from "styled-components";

const generateArrowStyle = (arrowPosition: ArrowPosition) => {
    return {
        size: 16,
        css: css`
            position: absolute;
            top: 50%;
            margin: 0;
            width: 35px;
            height: 35px;
            ${arrowPosition === "right" ? "right: 0" : "left:0"};
            background: ${color("common.ui3", 0.6)};
            transform: translate(0, -50%);
            & > ${IconWrapper} {
                color: ${color("common.white")};
                transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
                &:hover {
                    color: ${color("common.white")};
                }
            }
            z-index: 10;
            padding: 0;
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;
            border: 0;
            outline: none !important;

            &:focus {
                box-shadow: none;
            }
            &:hover {
                background: ${color("text.main", 0.6)};
            }

            ${(arrowPosition === "top" || arrowPosition === "bottom") &&
            css`
                position: relative;
                inset: 0;
                transform: none;
                margin: auto;
            `};

            &.swiper-button-disabled {
                background: ${color("common.ui3", 0.33)};
                & > ${IconWrapper} {
                    color: ${color("common.white")};
                }
            }
        `,
    };
};

const arrowNavigationIcons = {
    top: {
        src: ArrowUp,
        size: 16,
    },
    bottom: {
        src: ArrowDown,
        size: 16,
    },
    left: {
        src: CaretLeft,
        size: 16,
    },
    right: {
        src: CaretRight,
        size: 16,
    },
};

const getArrowPosition = (arrowType: ArrowType, direction: "horizontal" | "vertical" = "horizontal"): ArrowPosition => {
    const isVertical = direction === "vertical";
    if (arrowType === "next") {
        return isVertical ? "bottom" : "right";
    }
    // when arrowType='prev'.
    return isVertical ? "top" : "left";
};

const ArrowNavigation: FC<NavigationProps> = React.memo(
    ({ arrowType, showOnMobile, buttonIconProps, ...otherProps }) => {
        const swiperInstance = useSwiper();
        const arrowPosition = getArrowPosition(arrowType, swiperInstance?.isHorizontal() ? "horizontal" : "vertical");
        const { src: iconSrc, size: iconSize } = arrowNavigationIcons[arrowPosition];

        const {
            className,
            element: arrowElement,
            disabled,
        } = useArrowElement({
            swiper: swiperInstance,
            showOnMobile,
            arrowType,
        });

        const onClickHandler = useCallback(
            (event: any) => {
                arrowElement?.click();
                otherProps.onClick?.(event);
            },
            [arrowElement],
        );

        if (!swiperInstance || !arrowElement) {
            return null;
        }

        const props = {
            arrowPosition,
            showOnMobile,
            className: xClassNames.append(className, otherProps.className),
        };

        return (
            <Clickable
                {...props}
                {...generateArrowStyle(arrowPosition)}
                aria-label={`carousel-${arrowPosition}`}
                onClick={onClickHandler}
            >
                <ButtonIcon
                    src={iconSrc}
                    size={iconSize}
                    color="common.white"
                    className={`carousel-${arrowPosition}-icon ${disabled && "disabled"}`}
                    {...buttonIconProps}
                />
            </Clickable>
        );
    },
);

export default ArrowNavigation;
